import Phaser from 'phaser';
import { t, setLocale, getLocale, LOCALES, type Locale } from '@i18n/index';
import { PALETTE, TEXT_COLOUR, titleStyle, readoutStyle, FONT, px } from '../theme';
import { createButton } from '../ui/button';
import { generateSeed } from '@core/rng';
import { hasSave, loadCampaign, saveSettings } from '../saveGame';
import { audio } from '../audio/engine';
import { BASE_MOTIF, DRONE_ROOT } from '../audio/motifs';
import type { MapData } from '../mapData';
import { GAME_VERSION } from '../version';
import { SonarScope } from '../render/sonarScope';
import { createSettingsButton } from '../ui/settingsPanel';
import { addBackdrop, MENU_BACKDROP } from '../assets';

/**
 * Das Startmenue.
 *
 * Der Seed steht bewusst sichtbar davor: Er bestimmt den gesamten Durchlauf
 * einschliesslich der Aufloesung, und ihn weitergeben zu koennen macht aus
 * einem Einzelspiel eine Sache, ueber die man reden kann.
 */
export class MenuScene extends Phaser.Scene {
  private seed = generateSeed();
  private scope: SonarScope | null = null;
  private backdrop: Phaser.GameObjects.Image | null = null;

  constructor() {
    super('Menu');
  }

  create(): void {
    this.cameras.main.setBackgroundColor(PALETTE.abyss);

    // Der Schirm liegt hinter allem und wird beim Neuzeichnen nicht
    // mitgeloescht — sonst setzte der Strahl bei jedem Klick zurueck.
    const { width, height } = this.scale;
    this.backdrop = this.createBackdrop();
    this.scope = new SonarScope(
      this,
      width / 2,
      height * 0.46,
      Math.min(width, height) * 0.42,
    );

    this.redraw();
    this.cameras.main.fadeIn(500, 5, 8, 13);

    // Browser lassen Audio erst nach einer Nutzerhandlung zu. Der erste Klick
    // irgendwo im Menü genügt.
    this.input.once('pointerdown', () => {
      audio.resume();
      audio.startDrone(DRONE_ROOT.menu);
      audio.playMotif(BASE_MOTIF, 0.07);
    });
  }

  /**
   * Das gestrandete Wrack unter Sternen als Hintergrund.
   *
   * Es liegt hinter dem Sonarschirm und ist stark abgedunkelt — das Menue
   * bleibt eine Textseite, aber man weiss nach einer Sekunde, worum es in
   * diesem Spiel geht. Fehlt die Datei, bleibt es beim schwarzen Grund.
   */
  private createBackdrop(): Phaser.GameObjects.Image | null {
    return addBackdrop(this, MENU_BACKDROP, 0.42);
  }

  override update(_time: number, deltaMs: number): void {
    this.scope?.update(deltaMs, this.time.now);
  }

  private redraw(): void {
    // Nur die Bedienelemente raeumen, den Sonarschirm stehen lassen — sonst
    // setzte der Strahl bei jedem Klick zurueck.
    const keep = new Set<Phaser.GameObjects.GameObject>(this.scope?.objects ?? []);
    if (this.backdrop !== null) keep.add(this.backdrop);
    for (const child of [...this.children.list]) {
      if (!keep.has(child)) child.destroy();
    }

    const { width, height } = this.scale;
    const centreX = width / 2;

    this.add.text(centreX, height * 0.24, t('game.title'), titleStyle(38)).setOrigin(0.5);
    this.add
      .text(centreX, height * 0.24 + 44, t('menu.subtitle'), {
        ...readoutStyle(12, true),
        fontStyle: 'italic',
      })
      .setOrigin(0.5);

    this.add.rectangle(centreX, height * 0.24 + 74, 300, 1, PALETTE.panelEdge);

    // Dieselben Einstellungen wie im Spiel — hier zusaetzlich zu den Zeilen
    // unten, weil die Schriftgroesse dort nicht steht und man sie schon vor
    // dem ersten Text brauchen kann.
    createSettingsButton(this, this.scale.width - 24, 26, () => this.redraw());

    createButton(this, centreX, height * 0.46, t('menu.new'), () => {
      this.registry.set('pendingSeed', this.seed);
      this.scene.start('CharacterCreator');
    }, { width: 240 });

    if (hasSave()) {
      createButton(this, centreX, height * 0.46 + 54, t('menu.continue'), () => this.continueGame(), {
        width: 240,
      });
    }

    this.renderSeed(centreX, height * 0.68);
    this.renderLanguage(centreX, height * 0.68 + 46);
    this.renderSound(centreX, height * 0.68 + 84);

    // Impressum muss ohne Umwege erreichbar sein.
    const imprint = this.add
      .text(centreX, height - 34, t('menu.imprint'), {
        fontFamily: FONT.mono,
        fontSize: px(11),
        color: PALETTE.inkFaint,
      })
      .setOrigin(0.5)
      .setInteractive({ useHandCursor: true });

    imprint.on('pointerover', () => imprint.setColor(TEXT_COLOUR.coast));
    imprint.on('pointerout', () => imprint.setColor(PALETTE.inkFaint));
    imprint.on('pointerdown', () => this.scene.start('Imprint'));

    this.add
      .text(width - 14, height - 14, `v${GAME_VERSION}`, {
        fontFamily: FONT.mono,
        fontSize: px(10),
        color: PALETTE.inkFaint,
      })
      .setOrigin(1, 1);
  }

  private renderSeed(centreX: number, y: number): void {
    this.add
      .text(centreX - 110, y, t('menu.seed'), {
        fontFamily: FONT.mono,
        fontSize: px(11),
        color: PALETTE.inkFaint,
      })
      .setOrigin(1, 0.5);

    this.add
      .text(centreX - 90, y, this.seed, {
        fontFamily: FONT.mono,
        fontSize: px(15),
        color: TEXT_COLOUR.coast,
      })
      .setOrigin(0, 0.5);

    createButton(this, centreX + 130, y, t('menu.seed.random'), () => {
      this.seed = generateSeed();
      this.redraw();
    }, { width: 140, height: 28, fontSize: 11 });
  }

  private renderLanguage(centreX: number, y: number): void {
    this.add
      .text(centreX - 110, y, t('menu.language'), {
        fontFamily: FONT.mono,
        fontSize: px(11),
        color: PALETTE.inkFaint,
      })
      .setOrigin(1, 0.5);

    LOCALES.forEach((locale, index) => {
      const active = getLocale() === locale;
      const label = this.add
        .text(centreX - 84 + index * 46, y, locale.toUpperCase(), {
          fontFamily: FONT.mono,
          fontSize: px(13),
          color: active ? TEXT_COLOUR.coast : PALETTE.inkFaint,
        })
        .setOrigin(0, 0.5)
        .setInteractive({ useHandCursor: true });

      label.on('pointerdown', () => this.switchLanguage(locale));
    });
  }

  private renderSound(centreX: number, y: number): void {
    this.add
      .text(centreX - 110, y, t('menu.sound'), {
        fontFamily: FONT.mono,
        fontSize: px(11),
        color: PALETTE.inkFaint,
      })
      .setOrigin(1, 0.5);

    const label = this.add
      .text(centreX - 84, y, t(audio.isMuted ? 'menu.sound.off' : 'menu.sound.on'), {
        fontFamily: FONT.mono,
        fontSize: px(13),
        color: audio.isMuted ? PALETTE.inkFaint : TEXT_COLOUR.coast,
      })
      .setOrigin(0, 0.5)
      .setInteractive({ useHandCursor: true });

    label.on('pointerdown', () => {
      audio.resume();
      audio.setMuted(!audio.isMuted);
      saveSettings({ locale: getLocale(), muted: audio.isMuted });
      this.redraw();
    });
  }

  private switchLanguage(locale: Locale): void {
    setLocale(locale);
    saveSettings({ locale, muted: audio.isMuted });
    this.redraw();
  }

  private continueGame(): void {
    const mapData = this.registry.get('mapData') as MapData;
    const campaign = loadCampaign(mapData.bathymetry, mapData.coastline);

    if (campaign === null) {
      // Der Stand war unlesbar — dann bleibt nur ein neuer Anfang.
      this.redraw();
      return;
    }

    this.registry.set('campaign', campaign);
    this.scene.start('WorldMap');
  }
}
